import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { Download } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_FLIGHTS, DEMO_UIDS } from '../../lib/demoData';
import { sendAlert } from '../../lib/alerts';
import type { FlightDoc } from '../../lib/types';
import { PageHeader } from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import { DateRangeChips } from '../../components/DateRangeChips';
import { DATE_RANGES, inDateRange, type DateRange } from '../../lib/flightFilters';

export function MyFlights() {
  const { fbUser, demoMode } = useAuth();
  const [flights, setFlights] = useState<FlightDoc[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>('this_month');

  useEffect(() => {
    if (demoMode) {
      setFlights(DEMO_FLIGHTS.filter((f) => f.lesseeUid === DEMO_UIDS.brian).slice().reverse());
      return;
    }
    if (!db || !fbUser) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'flights'),
        where('lesseeUid', '==', fbUser.uid),
        orderBy('date', 'desc')
      ),
      (snap) => setFlights(snap.docs.map((d) => d.data() as FlightDoc))
    );
    return () => unsub();
  }, [fbUser, demoMode]);

  const all = flights ?? [];
  const filtered = all.filter((f) => inDateRange(f.date, range));
  const counts = Object.fromEntries(
    DATE_RANGES.map((r) => [r, all.filter((f) => inDateRange(f.date, r)).length])
  ) as Record<DateRange, number>;

  // Export reflects the current view (date range). Switch to "All" to export everything.
  function exportCsv() {
    if (!filtered.length) return;
    const rows = [
      ['Date', 'Aircraft', 'From', 'To', 'Hobbs Out', 'Hobbs In', 'Hours', 'PIC Hours', 'Pax', 'Notes'].join(','),
      ...filtered.map((f) =>
        [
          f.date,
          f.aircraftId,
          f.departureAirport,
          f.destinations.join('-'),
          f.hobbsOut,
          f.hobbsIn,
          f.hoursFlown,
          f.hoursFlown,
          f.passengerCount,
          JSON.stringify(f.notes ?? ''),
        ].join(',')
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flights-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader
        title="My flights"
        subtitle="Your logbook. Tap a flight for full detail."
        actions={
          <div className="flex gap-2">
            <button onClick={exportCsv} className="btn-secondary text-sm flex items-center gap-1.5">
              <Download size={14} /> Export CSV
            </button>
            <Link to="/flights/new" className="btn-primary text-sm">
              Log a flight
            </Link>
          </div>
        }
      />

      {!flights ? (
        <Spinner label="Loading flights" />
      ) : flights.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">No flights yet.</div>
      ) : (
        <>
          <div className="mb-4">
            <DateRangeChips value={range} onChange={setRange} counts={counts} />
          </div>
          <div className="mb-4 grid grid-cols-3 gap-3">
            <SummaryStat label="Flights" value={String(filtered.length)} />
            <SummaryStat label="Hobbs hrs" value={filtered.reduce((s, f) => s + f.hoursFlown, 0).toFixed(1)} />
            <SummaryStat label="Total charged" value={`$${filtered.reduce((s, f) => s + f.totalCharge, 0).toFixed(2)}`} />
          </div>
          {filtered.length === 0 ? (
            <div className="card p-8 text-center text-ink-muted">No flights in this range. Try “All”.</div>
          ) : (
          <>
          {/* Mobile: tap-to-expand cards (no cramped table) */}
          <div className="space-y-3 sm:hidden">
            {filtered.map((f) => {
              const isOpen = expanded === f.flightId;
              return (
                <div key={f.flightId} className="card overflow-hidden">
                  <button
                    onClick={() => setExpanded(isOpen ? null : f.flightId)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-display font-semibold text-navy-900">{f.date}</span>
                      <span className="font-display font-semibold text-navy-900">${f.totalCharge.toFixed(2)}</span>
                    </div>
                    <div className="mt-1 text-sm text-ink-muted">
                      {f.departureAirport} → {f.destinations.join(' → ')}
                    </div>
                    <div className="mt-1.5 flex items-center justify-between text-xs">
                      <span className="text-ink-muted">{f.hoursFlown.toFixed(1)} Hobbs hrs</span>
                      <span className="font-medium text-navy-700">{isOpen ? 'Hide details ▲' : 'View details ▾'}</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/60 p-4">
                      <FlightDetail f={f} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop / tablet: table */}
          <div className="card hidden overflow-x-auto sm:block">
            <table className="w-full text-sm">
              <thead className="bg-sky-50 text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Route</th>
                  <th className="px-4 py-3 font-medium">Hours</th>
                  <th className="px-4 py-3 font-medium">Charge</th>
                  <th className="px-4 py-3 font-medium text-right">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((f) => {
                  const isOpen = expanded === f.flightId;
                  return (
                    <Fragment key={f.flightId}>
                      <tr className="hover:bg-sky-50/50">
                        <td className="px-4 py-3 whitespace-nowrap">{f.date}</td>
                        <td className="px-4 py-3">
                          {f.departureAirport} → {f.destinations.join(' → ')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{f.hoursFlown.toFixed(1)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">${f.totalCharge.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setExpanded(isOpen ? null : f.flightId)}
                            className="text-navy-700 underline text-xs"
                          >
                            {isOpen ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-sky-50/40">
                          <td colSpan={5} className="px-4 py-4">
                            <FlightDetail f={f} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
          )}
        </>
      )}
    </>
  );
}

function FlightDetail({ f }: { f: FlightDoc }) {
  const { profile, demoMode } = useAuth();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);

  function send() {
    if (!demoMode) {
      sendAlert('flight_correction', {
        name: `${profile?.personal?.firstName ?? ''} ${profile?.personal?.lastName ?? ''}`.trim() || profile?.email,
        date: f.date,
        hobbs: `${f.hobbsOut.toFixed(1)} → ${f.hobbsIn.toFixed(1)}`,
        charge: `$${f.totalCharge.toFixed(2)}`,
        note: note.trim(),
      });
    }
    setSent(true);
    setOpen(false);
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
        <Detail label="Hobbs out" value={f.hobbsOut.toFixed(1)} />
        <Detail label="Hobbs in" value={f.hobbsIn.toFixed(1)} />
        <Detail label="Tach out" value={f.tachOut.toFixed(1)} />
        <Detail label="Tach in" value={f.tachIn.toFixed(1)} />
        <Detail label="Pax" value={String(f.passengerCount)} />
        {f.fuelLevelOnReturn && <Detail label="Fuel return" value={f.fuelLevelOnReturn} />}
        <Detail label="Away-from-base" value={`$${f.awayFromBaseCharge.toFixed(2)}`} />
        {typeof f.fuelCharge === 'number' && <Detail label="Fuel" value={`$${f.fuelCharge.toFixed(2)}`} />}
        {(f.fuelGallonsLeft != null || f.fuelGallonsRight != null) && (
          <Detail label="Fuel left L/R" value={`${f.fuelGallonsLeft ?? '—'} / ${f.fuelGallonsRight ?? '—'} gal`} />
        )}
        <Detail label="Charged" value={`$${f.totalCharge.toFixed(2)}`} />
        <Detail label="Stripe charge" value={f.stripeChargeId ?? '—'} mono />
      </div>
      {f.notes && (
        <div className="mt-3 text-sm">
          <span className="text-ink-muted">Notes: </span>
          {f.notes}
        </div>
      )}
      <div className="mt-3">
        {sent ? (
          <p className="text-xs text-emerald-700">✓ Correction request sent to the owner.</p>
        ) : open ? (
          <div className="space-y-2">
            <textarea
              className="input-textarea text-sm"
              placeholder="What needs correcting? (e.g., Hobbs in should be 4226.3, not 4226.5)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex gap-2">
              <button onClick={send} disabled={!note.trim()} className="btn-primary py-1.5 text-xs">
                Send to owner
              </button>
              <button onClick={() => setOpen(false)} className="btn-secondary py-1.5 text-xs">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setOpen(true)} className="text-xs font-medium text-navy-700 underline">
            Flag for owner correction
          </button>
        )}
      </div>
    </>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-3 text-center sm:p-4">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 font-display text-lg font-semibold text-navy-900 sm:text-xl">{value}</div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="uppercase tracking-wider text-ink-muted">{label}</div>
      <div className={mono ? 'font-mono text-ink' : 'text-ink font-medium'}>{value}</div>
    </div>
  );
}
